import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { presentationFor, LEGAL_DOCUMENTS } from '@lds-efsp/shared-contracts';
import type {
  TypedOrder,
  EfmFilingStatus,
  FilingDecisions,
  FeeWaiverTrigger,
  LegalDocumentId,
  OrderStatusUpdatesResponse,
} from '@lds-efsp/shared-contracts';
import { useBrand } from '../../../app/config/brand';
import { useApi, API_BASE_URL } from '../../../api/useApi';
import { useAuthStatus } from '../../../auth/useAuthStatus';
import { StatusBadge } from '../components/StatusBadge';
import OrderSummary from '../components/OrderSummary';
import { inDoubtCopyFor } from '../lib/in-doubt-copy';
import { useOrderStatusUpdates } from '../hooks/useOrderStatusUpdates';

const ORDER_TYPE_LABELS: Record<string, string> = {
  'e-filing': 'eFiling',
  'court-filing': 'Court Filing',
  'process-serving': 'Process Serving',
  'document-retrieval': 'Document Retrieval',
  'courtesy-copy-delivery': 'Courtesy Copy Delivery',
  'secretary-of-state-filing': 'Secretary of State Filing',
  'county-recording': 'County Recording',
  'skip-trace': 'Skip Trace',
  'eservice': 'eService',
};

interface EfmFiling {
  id: string;
  status: string;
  statusReason: string | null;
  externalFilingId: string | null;
  submittedAt: string | null;
  acceptedAt: string | null;
  rejectedAt: string | null;
  metadata: {
    caseNumber?: string;
    caseTitle?: string;
    messageToFiler?: string;
    conformedCopyUrls?: string[];
    courtResponse?: {
      statusCode?: string;
      statusMessage?: string;
    };
    fees?: {
      totalAmount: number;
      lineItems: Array<{ description: string; amount: number; category: string }>;
    };
    documentStatuses?: Array<{
      documentId: string;
      statusCode: string;
      statusMessage?: string;
    }>;
    documentMap?: Array<{
      controlId: number;
      fileName: string;
      orderDocumentId?: string;
    }>;
    // Accumulated per-filing clerk decisions (SPEC-SOR-78 R12), keyed by
    // orderDocumentId. Unlike documentStatuses (which only reflects the most
    // recent NFRC callback), this map survives split decisions arriving across
    // multiple callbacks in either order.
    filingDecisions?: FilingDecisions;
  } | null;
}

interface OrderWithFiling extends TypedOrder {
  efmFiling?: EfmFiling;
}

const FEE_WAIVER_TRIGGER_LABELS: Record<FeeWaiverTrigger, string> = {
  party_fw001_attached: 'Fee waiver request (FW-001) attached',
  party_on_file_asserted: 'Fee waiver on file (filer assertion)',
  account_pro_per: 'eFiling fee waiver account (Pro Per)',
};

const FILING_STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  submitted: 'Submitted to Court',
  under_review: 'Under Review',
  accepted: 'Accepted',
  partially_accepted: 'Partially Accepted',
  rejected: 'Rejected',
  error: 'Error',
};

const FILING_STATUS_COLORS: Record<string, string> = {
  pending: '#6B7280',
  submitted: '#3B82F6',
  under_review: '#F59E0B',
  accepted: '#22C55E',
  partially_accepted: '#F59E0B',
  rejected: '#EF4444',
  error: '#EF4444',
};

/** One rendered Status Timeline entry. */
export interface TimelineEntry {
  /** Stable React key: `created` for the derived first entry, else the row id. */
  key: string;
  /** The entry's full line — the label followed by the entry's own timestamp. */
  label: string;
}

/**
 * The Status Timeline's entries for an order: the derived `Created` line first,
 * then one entry per durable `order_status_updates` row, earliest-first by each
 * row's own timestamp.
 *
 * The `Created` entry is built from `order.createdAt` and is unconditional,
 * because no code path in this repository writes a Created status-update row —
 * it is the only Created entry the platform has, so an order that was never
 * submitted still reads as created. Every submission event comes from the rows
 * alone: the timeline never reads `order.submittedAt` or the EFM filing
 * timestamps, because an in-doubt submission leaves `submittedAt` deliberately
 * unstamped and would otherwise read as never submitted (SPEC-SOR-376 R12).
 *
 * Ordering by the rows' own timestamps rather than trusting the order the read
 * served them in is SPEC-SOR-376 R13: an earlier row always renders first.
 */
// eslint-disable-next-line react-refresh/only-export-components -- the timeline's entry mapping is this page's own contract, kept beside it
export function timelineEntriesFor(
  order: { createdAt: string },
  updates: OrderStatusUpdatesResponse | null,
): TimelineEntry[] {
  const rows = [...(updates?.updates ?? [])].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );
  return [
    { key: 'created', label: `Created ${new Date(order.createdAt).toLocaleString()}` },
    ...rows.map((row) => ({
      key: row.id,
      label: `${row.statusLabel} ${new Date(row.createdAt).toLocaleString()}`,
    })),
  ];
}

/** One rendered line of the Legal Versions at Submission panel. */
export interface LegalVersionLine {
  /** The governed document's registry id — also the line's stable React key. */
  id: LegalDocumentId;
  /** The full line: registry title, stamped version number, effective date. */
  label: string;
}

/**
 * The copy the panel shows for a submitted order carrying no stamps at all —
 * a submission that predates version stamping (SOR-377). Singular by design:
 * it reports the absence of the record, not one document's absence, so a
 * half-stamped order never prints it twice.
 */
export const LEGAL_VERSIONS_NOT_RECORDED = 'Not recorded — submitted before version stamping';

/** The stamp columns the panel reports, in the order it lists them. */
const GOVERNED_LEGAL_VERSIONS: ReadonlyArray<{
  id: LegalDocumentId;
  stamp: (order: {
    termsVersionAtSubmission?: number;
    privacyVersionAtSubmission?: number;
  }) => number | undefined;
}> = [
  { id: 'terms', stamp: (order) => order.termsVersionAtSubmission },
  { id: 'privacy', stamp: (order) => order.privacyVersionAtSubmission },
];

/**
 * The Legal Versions at Submission panel's lines, one per governed document
 * the order was stamped with: the registry's title for that document, the
 * stamped version number, and the version's effective date.
 *
 * Titles and effective dates come from the bundled `LEGAL_DOCUMENTS` registry
 * — the only source for either — looked up by the stamped number, so the panel
 * reports what the registry declares rather than restating it. A stamp the
 * registry does not declare renders its number with no date rather than
 * failing the page; the same arm covers a governed document missing from the
 * registry, which the bundled seed cannot produce (its validator holds the
 * registry to the full document set).
 *
 * A document whose stamp is absent contributes no line: the panel's caller
 * reads an empty result as "nothing recorded" and renders the distinct
 * not-recorded leg, so one present stamp renders one line, never the copy.
 */
// eslint-disable-next-line react-refresh/only-export-components -- the panel's line mapping is this page's own contract, kept beside it
export function legalVersionLinesFor(order: {
  termsVersionAtSubmission?: number;
  privacyVersionAtSubmission?: number;
}): LegalVersionLine[] {
  return GOVERNED_LEGAL_VERSIONS.flatMap(({ id, stamp }) => {
    const version = stamp(order);
    if (version == null) return [];
    const document = LEGAL_DOCUMENTS.find((entry) => entry.id === id);
    const declared = document?.versions.find((entry) => entry.version === version);
    const effectiveDate = declared ? ` (effective ${declared.effectiveDate.slice(0, 10)})` : '';
    return [{ id, label: `${document?.title ?? id} — version ${version}${effectiveDate}` }];
  });
}

interface OrderDetailPageProps {
  /** When rendered outside a route (e.g. inside AdminLayout), pass the id explicitly. */
  orderId?: string;
}

const OrderDetailPage: React.FC<OrderDetailPageProps> = ({ orderId }) => {
  const params = useParams<{ id: string }>();
  const id = orderId ?? params.id;
  const brand = useBrand();
  const navigate = useNavigate();
  const { get, post } = useApi();
  const { isPlatformUser } = useAuthStatus();

  const [order, setOrder] = useState<OrderWithFiling | null>(null);
  // The order's durable status-update rows, read through the one hook that owns
  // them: the status-preview popovers on the admin/operator surfaces read
  // through the same hook, so the timeline and the popovers cannot disagree
  // about one order. `id` is only ever read once it is set — the effect below
  // returns early and never calls the read without one.
  const { data: statusUpdates, fetchUpdates: fetchStatusUpdates } = useOrderStatusUpdates(id ?? '');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadingDoc, setDownloadingDoc] = useState<string | null>(null);
  const [refilingInProgress, setRefilingInProgress] = useState(false);

  const handleDownload = async (orderId: string, docId: string) => {
    setDownloadingDoc(docId);
    try {
      const data = await get<{ url: string }>(`${API_BASE_URL}/api/orders/${orderId}/documents/${docId}/download`);
      window.open(data.url, '_blank');
    } catch {
      // Silently fail — user can try again
    } finally {
      setDownloadingDoc(null);
    }
  };

  const handleRefile = async () => {
    if (!order?.id) return;
    setRefilingInProgress(true);
    try {
      const newOrder = await post<TypedOrder>(`${API_BASE_URL}/api/orders/${order.id}/refile`, {});
      localStorage.setItem('efilingDraft', JSON.stringify({ wizardState: newOrder.typeData, orderId: newOrder.id }));
      navigate(isPlatformUser ? '/admin/efiling' : '/eFiling');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create refile draft');
      setRefilingInProgress(false);
    }
  };

  /* eslint-disable @eslint-react/set-state-in-effect -- loading flag before async fetch */
  useEffect(() => {
    if (!id) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- TODO(deps-2026-07): newly detected by react-hooks bump, burn-down separately
    setLoading(true);
    get<OrderWithFiling>(`${API_BASE_URL}/api/orders/${id}`)
      .then(setOrder)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
    // The order's durable status-update rows — the Status Timeline's source of
    // truth (SPEC-SOR-376 R12). Issued after the order read, so the order stays
    // the first request this effect makes. The read stays best-effort and is
    // deliberately NOT wired into the page-level error state: the hook absorbs a
    // failure into its own error state, so a failed or empty read leaves every
    // other block on the page as it is, the timeline showing only its derived
    // Created entry.
    void fetchStatusUpdates();
  }, [id, get, fetchStatusUpdates]);
  /* eslint-enable @eslint-react/set-state-in-effect */

  if (loading) {
    return (
      <div className="p-6">
        <p style={{ color: brand.colors.textLight }}>Loading order...</p>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="p-6">
        <p className="text-red-500">{error ?? 'Order not found'}</p>
        <button
          onClick={() => navigate(isPlatformUser ? '/admin/orders' : '/orders')}
          className="mt-4 text-sm underline"
          style={{ color: brand.colors.link }}
        >
          Back to Orders
        </button>
      </div>
    );
  }

  const isDraft = order.status === 'draft';
  const efm = order.efmFiling;
  // The submission banner is derived strictly from the persisted order status
  // (and EFM filing status for the rejected/partial sub-detail) via the pure
  // selector — never from a navigation/query-string flag. SUCCESS (the green
  // banner) is reachable only for an `accepted` order. REJECTED/PARTIALLY_ACCEPTED
  // are left to the dedicated rejection block below, so this block gates on only
  // the four enumerated presentations to avoid rendering two banners at once.
  const presentation = presentationFor(order.status, efm?.status as EfmFilingStatus | undefined);
  // Who is reading decides the pending-confirmation banner's voice (SPEC-SOR-376
  // R14): a platform operator is looking at an ACCOUNT'S order and reads copy
  // about the order, while the filer keeps the second-person copy. The viewer
  // class is this page's existing `isPlatformUser` audience predicate — the same
  // split the fee-waiver basis panel below gates on — mapped here so the copy
  // decider itself stays free of any auth read.
  const inDoubtCopy = inDoubtCopyFor(isPlatformUser ? 'operator' : 'customer');

  return (
    <div className="p-6">
      {/*
        The title block leads the page: the h1 must be the FIRST heading in
        document order so a headings-navigation user meets the page title
        before any status banner. The banners below are content (they keep their
        h2 level and wording) — never re-insert one above this block.
      */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <h1
            className="text-2xl font-bold"
            style={{ color: brand.colors.text }}
          >
            Order {order.referenceNumber}
          </h1>
          <p className="text-sm mt-1" style={{ color: brand.colors.textLight }}>
            {ORDER_TYPE_LABELS[order.type] ?? order.type}
          </p>
        </div>
        <StatusBadge status={order.status} brand={brand} />
      </div>

      {presentation === 'SUCCESS' && (
        <div
          className="mb-6 p-4 rounded-lg border"
          style={{
            backgroundColor: '#F0FDF4',
            borderColor: '#BBF7D0',
          }}
        >
          <div className="flex items-center gap-3">
            <svg className="w-6 h-6 text-green-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h2 className="text-lg font-semibold text-green-800">
                Order Submitted Successfully
              </h2>
              <p className="text-sm text-green-700">
                Your order <strong>{order.referenceNumber}</strong> has been accepted by the court.
              </p>
            </div>
          </div>
        </div>
      )}

      {presentation === 'PENDING_CONFIRMATION' && (
        <div
          className="mb-6 p-4 rounded-lg border"
          style={{
            backgroundColor: '#EFF6FF',
            borderColor: '#BFDBFE',
          }}
        >
          <div className="flex items-center gap-3">
            <svg className="w-6 h-6 text-blue-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h2 className="text-lg font-semibold text-blue-800">
                Submission pending court confirmation
              </h2>
              <p className="text-sm text-blue-700">
                {inDoubtCopy.beforeReference}<strong>{order.referenceNumber}</strong>{inDoubtCopy.afterReference}
              </p>
            </div>
          </div>
        </div>
      )}

      {presentation === 'NEEDS_ATTENTION' && (
        <div
          className="mb-6 p-4 rounded-lg border"
          style={{
            backgroundColor: '#FFFBEB',
            borderColor: '#FDE68A',
          }}
        >
          <div className="flex items-center gap-3">
            <svg className="w-6 h-6 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="#D97706">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <div>
              <h2 className="text-lg font-semibold" style={{ color: '#92400E' }}>
                Submission needs attention
              </h2>
              <p className="text-sm" style={{ color: '#B45309' }}>
                We couldn&apos;t confirm your order <strong>{order.referenceNumber}</strong> with the court
                and it has been escalated for review. Our team is looking into it.
              </p>
            </div>
          </div>
        </div>
      )}

      {presentation === 'AWAITING_REVIEW' && (
        <div
          className="mb-6 p-4 rounded-lg border"
          style={{
            backgroundColor: '#F9FAFB',
            borderColor: '#E5E7EB',
          }}
        >
          <div className="flex items-center gap-3">
            <svg className="w-6 h-6 text-gray-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h2 className="text-lg font-semibold" style={{ color: brand.colors.text }}>
                Submitted, awaiting court review
              </h2>
              <p className="text-sm" style={{ color: brand.colors.textLight }}>
                Your order <strong>{order.referenceNumber}</strong> has been submitted and is awaiting
                court review.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Order details */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          {/* Case info */}
          <div
            className="border rounded-lg p-4"
            style={{ borderColor: brand.colors.border }}
          >
            <h2 className="text-sm font-semibold mb-3" style={{ color: brand.colors.text }}>
              Order Information
            </h2>
            <div className="grid grid-cols-2 gap-3">
              {(order.caseNumber || efm?.metadata?.caseNumber) && (
                <div>
                  <p className="text-xs" style={{ color: brand.colors.textLight }}>Case Number</p>
                  <p className="text-sm font-mono" style={{ color: brand.colors.text }}>{order.caseNumber || efm?.metadata?.caseNumber}</p>
                </div>
              )}
              {order.caseTitle && (
                <div>
                  <p className="text-xs" style={{ color: brand.colors.textLight }}>Case Title</p>
                  <p className="text-sm" style={{ color: brand.colors.text }}>{order.caseTitle}</p>
                </div>
              )}
              {order.courtLocation && (
                <div>
                  <p className="text-xs" style={{ color: brand.colors.textLight }}>Court Location</p>
                  <p className="text-sm" style={{ color: brand.colors.text }}>{order.courtLocation}</p>
                </div>
              )}
              <div>
                <p className="text-xs" style={{ color: brand.colors.textLight }}>Priority</p>
                <p className="text-sm capitalize" style={{ color: brand.colors.text }}>{order.priority}</p>
              </div>
              {order.totalAmount != null && (
                <div>
                  <p className="text-xs" style={{ color: brand.colors.textLight }}>Total Amount</p>
                  <p className="text-sm font-medium" style={{ color: brand.colors.text }}>
                    ${order.totalAmount.toFixed(2)}
                  </p>
                </div>
              )}
            </div>
            {order.notes && (
              <div className="mt-3">
                <p className="text-xs" style={{ color: brand.colors.textLight }}>Notes</p>
                <p className="text-sm" style={{ color: brand.colors.text }}>{order.notes}</p>
              </div>
            )}
          </div>

          {/* Filing Summary (e-filing orders) */}
          {order.type === 'e-filing' && order.typeData && (
            <OrderSummary typeData={order.typeData} brand={brand} />
          )}

          {/* Fee Waiver Basis (platform/operator only — SOR-251). The persisted
              record explains a $0 charge; operators are the audience that asks
              "why was this order free?" Filers never see it. Pointer values are
              rendered read-only: the record names evidence, it never embeds it. */}
          {isPlatformUser && order.feeWaiverBasis && (() => {
            const basis = order.feeWaiverBasis;
            const td = order.typeData as Record<string, unknown> | undefined;
            const parties = (td?.parties ?? []) as Array<Record<string, unknown>>;
            const firing = parties.find(p => p.id === basis.partyId);
            const partyName = firing
              ? [firing.firstName, firing.lastName].filter(Boolean).join(' ') || (firing.organizationName as string | undefined)
              : undefined;
            const fw001 = order.documents?.find(d => d.id === basis.fw001DocumentId);
            return (
              <div
                className="border rounded-lg p-4"
                style={{ borderColor: brand.colors.border }}
                data-testid="fee-waiver-basis"
              >
                <h2 className="text-sm font-semibold mb-3" style={{ color: brand.colors.text }}>
                  Fee Waiver Basis
                </h2>
                <div className="space-y-2">
                  <div>
                    <p className="text-xs" style={{ color: brand.colors.textLight }}>Trigger</p>
                    <p className="text-sm" style={{ color: brand.colors.text }}>
                      {FEE_WAIVER_TRIGGER_LABELS[basis.trigger] ?? basis.trigger}
                    </p>
                  </div>
                  {basis.partyId && (
                    <div>
                      <p className="text-xs" style={{ color: brand.colors.textLight }}>Filing party</p>
                      <p className="text-sm" style={{ color: brand.colors.text }}>
                        {partyName ?? basis.partyId}
                      </p>
                    </div>
                  )}
                  {basis.fw001DocumentId && (
                    <div>
                      <p className="text-xs" style={{ color: brand.colors.textLight }}>Fee waiver request</p>
                      <p className="text-sm font-mono" style={{ color: brand.colors.text }}>
                        {fw001?.fileName ?? basis.fw001DocumentId}
                      </p>
                    </div>
                  )}
                  {basis.courtReturnedFieldsRef === 'reported' && (
                    <div>
                      <p className="text-xs" style={{ color: brand.colors.textLight }}>Court-returned waiver fields</p>
                      <p className="text-sm" style={{ color: brand.colors.text }}>Reported by the court</p>
                    </div>
                  )}
                  {basis.acceptanceRef && (
                    <div>
                      <p className="text-xs" style={{ color: brand.colors.textLight }}>NFRC acceptance reference</p>
                      <p className="text-sm font-mono" style={{ color: brand.colors.text }}>{basis.acceptanceRef}</p>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Legal Versions at Submission (platform/operator only — SOR-378).
              The stamps are the per-order half of the dispute record: which
              Terms and Privacy Policy versions were in force the instant this
              submission committed. Same audience as the fee-waiver basis panel
              above and the same read-only rendering — operators ask what the
              order was bound by, filers never see it, and a draft (which by
              construction carries no stamp) shows nothing at all. */}
          {isPlatformUser && !isDraft && (() => {
            const lines = legalVersionLinesFor(order);
            return (
              <div
                className="border rounded-lg p-4"
                style={{ borderColor: brand.colors.border }}
                data-testid="legal-versions-at-submission"
              >
                <h2 className="text-sm font-semibold mb-3" style={{ color: brand.colors.text }}>
                  Legal Versions at Submission
                </h2>
                {lines.length === 0 ? (
                  <p className="text-sm" style={{ color: brand.colors.textLight }}>
                    {LEGAL_VERSIONS_NOT_RECORDED}
                  </p>
                ) : (
                  <div className="space-y-2">
                    {lines.map((line) => (
                      <p key={line.id} className="text-sm" style={{ color: brand.colors.text }}>
                        {line.label}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Rejection Banner */}
          {efm && (efm.status === 'rejected' || efm.status === 'partially_accepted') && (efm.statusReason || efm.metadata?.messageToFiler || Object.keys(efm.metadata?.filingDecisions ?? {}).length > 0) && (
            <div
              className="p-4 rounded-lg border"
              style={{
                backgroundColor: efm.status === 'rejected' ? '#FEF2F2' : '#FFFBEB',
                borderColor: efm.status === 'rejected' ? '#FECACA' : '#FDE68A',
              }}
            >
              <div className="flex items-start gap-3">
                <svg className="w-6 h-6 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke={efm.status === 'rejected' ? '#DC2626' : '#D97706'}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <div className="flex-1">
                  <h2 className="text-base font-semibold" style={{ color: efm.status === 'rejected' ? '#991B1B' : '#92400E' }}>
                    {efm.status === 'rejected' ? 'Filing Rejected by Court' : 'Filing Partially Accepted'}
                  </h2>
                  <p className="text-sm mt-1" style={{ color: efm.status === 'rejected' ? '#B91C1C' : '#B45309' }}>
                    {efm.metadata?.messageToFiler || efm.statusReason}
                  </p>
                  {/* Per-filing outcomes from the accumulated decision map (R12):
                      accepted filings show their court case number, rejected
                      filings show the clerk's rejection reason. Preferred over
                      documentStatuses because it accumulates across split
                      decisions arriving in separate NFRC callbacks. */}
                  {efm.metadata?.filingDecisions && Object.keys(efm.metadata.filingDecisions).length > 0 ? (() => {
                    const docMap = efm.metadata?.documentMap;
                    const decisions = Object.entries(efm.metadata.filingDecisions);
                    const resolveName = (orderDocumentId: string) =>
                      docMap?.find((dm) => dm.orderDocumentId === orderDocumentId)?.fileName ?? `Document ${orderDocumentId.slice(0, 8)}`;
                    return (
                      <div className="mt-3 space-y-1.5" data-testid="filing-decisions">
                        {decisions.map(([orderDocumentId, decision]) => {
                          const isAccepted = decision.outcome === 'accepted';
                          const detail = isAccepted
                            ? (decision.caseNumber ? `Case #${decision.caseNumber}` : '')
                            : (decision.rejectionReason ?? '');
                          return (
                            <div key={orderDocumentId} className="flex items-center gap-2 text-xs" data-testid={`filing-decision-${decision.outcome}`}>
                              <span
                                className="inline-flex items-center px-1.5 py-0.5 rounded font-medium"
                                style={{
                                  backgroundColor: isAccepted ? '#DCFCE7' : '#FEE2E2',
                                  color: isAccepted ? '#166534' : '#991B1B',
                                }}
                              >
                                {isAccepted ? 'Accepted' : 'Rejected'}
                              </span>
                              <span style={{ color: efm.status === 'rejected' ? '#B91C1C' : '#B45309' }}>
                                {resolveName(orderDocumentId)}{detail ? ` — ${detail}` : ''}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })() : efm.metadata?.documentStatuses && efm.metadata.documentStatuses.length > 0 && (() => {
                    const docMap = efm.metadata?.documentMap as Array<{ controlId: number; fileName: string }> | undefined;
                    const resolveName = (ds: { documentId: string }) =>
                      docMap?.find((dm) => String(dm.controlId) === ds.documentId)?.fileName ?? `Document ${ds.documentId}`;
                    // Disambiguate identical filenames (e.g. an original + a resubmit can share a
                    // name) so the same name doesn't appear once Accepted and once Rejected and read
                    // as a contradiction.
                    const nameCounts = efm.metadata.documentStatuses.reduce<Record<string, number>>((acc, ds) => {
                      const n = resolveName(ds);
                      acc[n] = (acc[n] ?? 0) + 1;
                      return acc;
                    }, {});
                    return (
                      <div className="mt-3 space-y-1.5">
                        {efm.metadata.documentStatuses.map((ds) => {
                          const baseName = resolveName(ds);
                          const docName = nameCounts[baseName] > 1 ? `${baseName} (#${ds.documentId})` : baseName;
                          const isAccepted = ds.statusCode === 'AC' || ds.statusCode === 'ACCEPTED';
                          return (
                            <div key={`${ds.documentId}-${ds.statusCode}`} className="flex items-center gap-2 text-xs">
                              <span
                                className="inline-flex items-center px-1.5 py-0.5 rounded font-medium"
                                style={{
                                  backgroundColor: isAccepted ? '#DCFCE7' : '#FEE2E2',
                                  color: isAccepted ? '#166534' : '#991B1B',
                                }}
                              >
                                {isAccepted ? 'Accepted' : 'Rejected'}
                              </span>
                              <span style={{ color: efm.status === 'rejected' ? '#B91C1C' : '#B45309' }}>
                                {docName}{ds.statusMessage ? ` — ${ds.statusMessage}` : ''}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                  {order.type === 'e-filing' && (
                    <button
                      onClick={handleRefile}
                      disabled={refilingInProgress}
                      className="mt-4 px-4 py-2 rounded-lg text-sm font-medium transition-opacity disabled:opacity-50"
                      style={{ backgroundColor: brand.colors.primary, color: brand.colors.onPrimary }}
                    >
                      {refilingInProgress ? 'Creating draft...' : 'Correct & Refile'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* EFM Filing Status */}
          {efm && (
            <div
              className="border rounded-lg p-4"
              style={{ borderColor: brand.colors.border }}
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold" style={{ color: brand.colors.text }}>
                  Court Filing Status
                </h3>
                <span
                  className="px-2 py-0.5 rounded-full text-xs font-medium text-white"
                  style={{ backgroundColor: FILING_STATUS_COLORS[efm.status] || '#6B7280' }}
                >
                  {FILING_STATUS_LABELS[efm.status] || efm.status}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {(efm.metadata?.caseNumber || efm.metadata?.caseTitle) && (
                  <div className="col-span-2">
                    <p className="text-xs" style={{ color: brand.colors.textLight }}>Case Number</p>
                    <p className="text-lg font-mono font-semibold" style={{ color: brand.colors.text }}>
                      {efm.metadata.caseNumber}
                    </p>
                    {efm.metadata.caseTitle && (
                      <p className="text-sm mt-0.5" style={{ color: brand.colors.textLight }}>
                        {efm.metadata.caseTitle}
                      </p>
                    )}
                  </div>
                )}
                {efm.externalFilingId && (
                  <div>
                    <p className="text-xs" style={{ color: brand.colors.textLight }}>Filing ID</p>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-mono" style={{ color: brand.colors.text }}>{efm.externalFilingId}</p>
                      <button
                        onClick={() => { navigator.clipboard.writeText(efm.externalFilingId); }}
                        className="px-1.5 py-0.5 rounded text-xs border hover:bg-gray-50"
                        style={{ borderColor: brand.colors.border, color: brand.colors.textLight }}
                        title="Copy Filing ID"
                      >
                        Copy
                      </button>
                    </div>
                  </div>
                )}
                {efm.metadata?.courtResponse?.statusMessage && (
                  <div className="col-span-2">
                    <p className="text-xs" style={{ color: brand.colors.textLight }}>Court Response</p>
                    <p className="text-sm" style={{ color: brand.colors.text }}>
                      {efm.metadata.courtResponse.statusMessage}
                    </p>
                  </div>
                )}
              </div>

              {/* Court Fees */}
              {efm.metadata?.fees && (
                <div className="mt-3 pt-3 border-t" style={{ borderColor: brand.colors.border }}>
                  <p className="text-xs font-medium mb-1" style={{ color: brand.colors.textLight }}>Court Fees</p>
                  {efm.metadata.fees.lineItems.map((item) => (
                    <div key={`${item.description}-${item.amount}`} className="flex justify-between text-sm">
                      <span style={{ color: brand.colors.text }}>{item.description}</span>
                      <span className="font-medium" style={{ color: brand.colors.text }}>
                        ${item.amount.toFixed(2)}
                      </span>
                    </div>
                  ))}
                  <div className="flex justify-between text-sm font-semibold mt-1 pt-1 border-t" style={{ borderColor: brand.colors.border }}>
                    <span style={{ color: brand.colors.text }}>Total</span>
                    <span style={{ color: brand.colors.text }}>${efm.metadata.fees.totalAmount.toFixed(2)}</span>
                  </div>
                </div>
              )}

              {/* Conformed Copies — prefer GCS-stored docs, fallback to raw JTI URLs */}
              {(() => {
                const conformedDocs = order.documents?.filter(d => d.category === 'conformed-copy') || [];
                const hasStoredCopies = conformedDocs.length > 0;
                const hasRawUrls = !hasStoredCopies && efm.metadata?.conformedCopyUrls && efm.metadata.conformedCopyUrls.length > 0;

                if (!hasStoredCopies && !hasRawUrls) return null;

                return (
                  <div className="mt-3 pt-3 border-t" style={{ borderColor: brand.colors.border }}>
                    <p className="text-xs font-medium mb-2" style={{ color: brand.colors.textLight }}>
                      Conformed Copies (court-stamped)
                    </p>
                    <div className="space-y-1">
                      {hasStoredCopies ? (
                        conformedDocs.map(doc => (
                          <div key={doc.id} className="flex items-center justify-between">
                            <span className="text-sm" style={{ color: brand.colors.text }}>
                              {doc.description || doc.fileName}
                            </span>
                            <button
                              onClick={() => handleDownload(order.id, doc.id)}
                              disabled={downloadingDoc === doc.id}
                              className="px-3 py-1 rounded text-xs font-medium border"
                              style={{ borderColor: brand.colors.border, color: brand.colors.link }}
                            >
                              {downloadingDoc === doc.id ? 'Opening...' : 'Download'}
                            </button>
                          </div>
                        ))
                      ) : (
                        efm.metadata.conformedCopyUrls.map((url, i) => (
                          <a
                            key={url}
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-sm underline"
                            style={{ color: brand.colors.link }}
                          >
                            Conformed Copy {i + 1}
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                          </a>
                        ))
                      )}
                    </div>
                    {hasRawUrls && (
                      <p className="text-xs mt-1" style={{ color: brand.colors.textLight }}>
                        Links expire 7 days after court acceptance
                      </p>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          {/* Submission Debug Info */}
          {efm && order.typeData && (
            <details
              className="border rounded-lg p-4"
              style={{ borderColor: brand.colors.border }}
            >
              <summary className="text-sm font-semibold cursor-pointer" style={{ color: brand.colors.text }}>
                Submission Details (for JTI coordination)
              </summary>
              <div className="mt-3 space-y-2 text-xs font-mono" style={{ color: brand.colors.textLight }}>
                {(() => {
                  const td = order.typeData as Record<string, unknown>;
                  const caseInfo = td.caseInfo as Record<string, unknown> | undefined;
                  const parties = (td.parties || []) as Array<Record<string, unknown>>;
                  const docs = (td.documents || []) as Array<Record<string, unknown>>;
                  const orderInfo = td.orderInfo as Record<string, unknown> | undefined;
                  const caseType = caseInfo?.caseType as Record<string, unknown> | undefined;
                  return (
                    <>
                      <div><span className="font-semibold">Court:</span> {orderInfo?.county as string || '—'}</div>
                      <div><span className="font-semibold">Filing Type:</span> {orderInfo?.filingType as string || '—'}</div>
                      <div><span className="font-semibold">Case Title:</span> {caseInfo?.caseTitle as string || '—'}</div>
                      <div><span className="font-semibold">Case Category:</span> {caseInfo?.caseCategoryName as string || '—'} ({caseInfo?.caseCategoryCode as string || '—'})</div>
                      <div><span className="font-semibold">Case Type:</span> {caseType?.name as string || '—'} ({caseType?.code as string || '—'})</div>
                      <div><span className="font-semibold">Incident Zip:</span> {caseInfo?.incidentZip as string || '—'}</div>
                      <div className="mt-2"><span className="font-semibold">Parties ({parties.length}):</span></div>
                      {parties.map((p) => (
                        <div key={`${p.roleCode}-${p.firstName}-${p.lastName}`} className="ml-3">
                          {p.firstName as string} {p.lastName as string} — {p.roleName as string} ({p.roleCode as string})
                          {p.isSelfRepresented ? ' [Self-Rep]' : ''}
                        </div>
                      ))}
                      <div className="mt-2"><span className="font-semibold">Documents ({docs.length}):</span></div>
                      {docs.map((d) => (
                        <div key={`${(d.documentType as Record<string, unknown>)?.name ?? d.name}`} className="ml-3">
                          {(d.documentType as Record<string, unknown>)?.name as string || d.name as string}
                          {d.isLeadDocument ? ' [Lead]' : ''}
                        </div>
                      ))}
                      {efm.externalFilingId && (
                        <div className="mt-2 p-2 rounded bg-blue-50 text-blue-800 text-xs">
                          Tell JTI: Filing ID <span className="font-bold">{efm.externalFilingId}</span> — submitted {efm.submittedAt ? new Date(efm.submittedAt).toLocaleString() : '—'}
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </details>
          )}

          {/* Documents (exclude conformed copies — shown in their own section above) */}
          {order.documents && order.documents.length > 0 && (() => {
            const filingDocs = order.documents.filter(d => d.category !== 'conformed-copy');
            if (filingDocs.length === 0) return null;
            return (
            <div
              className="border rounded-lg p-4"
              style={{ borderColor: brand.colors.border }}
            >
              <h2 className="text-sm font-semibold mb-3" style={{ color: brand.colors.text }}>
                Documents ({filingDocs.length})
              </h2>
              <div className="space-y-2">
                {filingDocs.map(doc => (
                  <div
                    key={doc.id}
                    className="flex items-center justify-between p-2 rounded"
                    style={{ backgroundColor: brand.colors.bgLight || '#F9FAFB' }}
                  >
                    <div>
                      <p className="text-sm" style={{ color: brand.colors.text }}>{doc.fileName}</p>
                      <p className="text-xs" style={{ color: brand.colors.textLight }}>
                        {(doc.size / 1024).toFixed(1)} KB
                        {doc.documentType ? ` \u00B7 ${doc.documentType.name}` : ''}
                        {doc.isLeadDocument ? ' \u00B7 Lead' : ''}
                      </p>
                    </div>
                    {doc.gcsPath && (
                      <button
                        onClick={() => handleDownload(order.id, doc.id)}
                        disabled={downloadingDoc === doc.id}
                        className="px-3 py-1 rounded text-xs font-medium border"
                        style={{ borderColor: brand.colors.border, color: brand.colors.link }}
                      >
                        {downloadingDoc === doc.id ? 'Opening...' : 'Download'}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
            );
          })()}
        </div>

        {/* Sidebar */}
        <div>
          <div
            className="border rounded-lg p-4 sticky top-6"
            style={{ borderColor: brand.colors.border }}
          >
            <h2 className="text-sm font-semibold mb-3" style={{ color: brand.colors.text }}>
              Actions
            </h2>
            {isDraft && (
              <button
                onClick={() =>
                  // e-filing drafts resume in the wizard, which rehydrates the
                  // saved state from the server via ?orderId=. Other order
                  // types still go through the new-order form.
                  order.type === 'e-filing'
                    ? navigate(`${isPlatformUser ? '/admin/efiling' : '/eFiling'}?orderId=${order.id}`)
                    : navigate(`/orders/new/${order.type}`)
                }
                className="w-full px-4 py-2 rounded-lg text-sm font-medium mb-2"
                style={{ backgroundColor: brand.colors.primary, color: brand.colors.onPrimary }}
              >
                Edit Draft
              </button>
            )}
            <button
              onClick={() => navigate('/orders')}
              className="w-full px-4 py-2 rounded-lg text-sm font-medium border"
              style={{
                borderColor: brand.colors.border,
                color: brand.colors.text,
              }}
            >
              Back to Orders
            </button>
          </div>

          {/* Timeline */}
          <div
            className="border rounded-lg p-4 mt-4"
            style={{ borderColor: brand.colors.border }}
          >
            <h2 className="text-sm font-semibold mb-3" style={{ color: brand.colors.text }}>
              Status Timeline
            </h2>
            <div className="space-y-2">
              {timelineEntriesFor(order, statusUpdates).map((entry, index) => (
                <div key={entry.key} className="flex items-center gap-2">
                  <div
                    className="w-2 h-2 rounded-full"
                    style={{
                      // The derived Created entry always leads (see
                      // `timelineEntriesFor`) and keeps its own tone; every
                      // status-update row shares the primary tone.
                      backgroundColor: index === 0
                        ? brand.colors.success || '#22C55E'
                        : brand.colors.primary,
                    }}
                  />
                  <p className="text-xs" style={{ color: brand.colors.text }}>
                    {entry.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailPage;
